import { Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { PosBatchesTab } from "../../../features/treasury/pos/batches.js";
import { PosCommissionTab, PosValueCalendarTab } from "../../../features/treasury/pos/reports.js";
import { PosSettlementTab } from "../../../features/treasury/pos/settlement.js";
import { PosTerminalsTab } from "../../../features/treasury/pos/terminals.js";
import { TreasuryPage } from "../../../features/treasury/shared.js";

const TABS = ["terminals", "batches", "settlement", "calendar", "commission"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/finans/pos")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PosPage,
});

function PosPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab: TabKey = Route.useSearch().tab ?? "batches";

  return (
    <TreasuryPage title={t("menu.pos")} description={t("treasury.pos.description")}>
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ company: previous.company, tab: next as TabKey }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.pos")} className="flex-wrap">
            {TABS.map((key) => (
              <TabsTrigger key={key} value={key}>
                {t(`treasury.pos.tabs.${key}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="terminals">
            {tab === "terminals" ? <PosTerminalsTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="batches">
            {tab === "batches" ? <PosBatchesTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="settlement">
            {tab === "settlement" ? <PosSettlementTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="calendar">
            {tab === "calendar" ? <PosValueCalendarTab companyId={companyId} /> : null}
          </TabsContent>
          <TabsContent value="commission">
            {tab === "commission" ? <PosCommissionTab companyId={companyId} /> : null}
          </TabsContent>
        </Tabs>
      )}
    </TreasuryPage>
  );
}
